import { createFileRoute } from "@tanstack/react-router";
import { DEMOGRAPHIC_CATEGORIES } from "@alm/validation";
import { relationshipLabel } from "@alm/domain";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { guardRoute } from "@/lib/policy/route-guard";
import { DemographicListSection } from "./-components/care/demographic-list-section";
import { DemographicMismatchCard } from "./-components/care/demographic-mismatch-card";
import {
  useDemographicOptions,
  type DemographicCategory,
} from "./-components/care/use-demographic-options";

export const Route = createFileRoute("/_authenticated/_administration/settings/demographic-lists")({
  beforeLoad: ({ context }) => guardRoute(context.me, "demographic-lists"),
  component: ResidentListsSettings,
});

type List = { key: DemographicCategory; title: string; description: string };

const DESCRIPTIONS: Record<string, string> = {
  religion: "What a resident's faith is recorded as, and what the chaplain's list is drawn from.",
  ethnicity: "Recorded for the resident's own record and for state reporting.",
  language: "The language somebody is spoken to in, which is read off the chart at the bedside.",
};

const DEMOGRAPHICS: readonly List[] = DEMOGRAPHIC_CATEGORIES.map((category) => ({
  key: category,
  title: relationshipLabel(category),
  description: DESCRIPTIONS[category] ?? "",
}));

const PROFILE: readonly List[] = [
  {
    key: "payment_mode",
    title: "Payment mode",
    description: "How a resident's stay is paid for, like private pay or long-term care insurance.",
  },
  {
    key: "showers",
    title: "Showers",
    description: "How much help a resident needs in the shower.",
  },
  {
    key: "room_cleaning",
    title: "Room cleaning",
    description: "Who cleans a resident's room, and how often.",
  },
];

/** Every list staff pick from on a resident's chart and profile, kept per building. The catalog
 * stops free-text variants ("Catholic", "catholic"); the mismatch card finds chart values the
 * demographic lists lack and rewrites them, counting first. */
function ResidentListsSettings() {
  const catalog = useDemographicOptions();
  const { realFacility, options, remove, removing, setRemoving } = catalog;

  const header = (
    <PageHeader
      title="Resident lists"
      meta="The choices staff pick on a resident's chart: religion, ethnicity and language, how a stay is paid for, showers and room cleaning."
    />
  );

  if (!realFacility) {
    return (
      <>
        {header}
        <OneBuildingCard prompt="These lists are kept per building. Choose the building to edit its lists." />
      </>
    );
  }

  return (
    <>
      {header}
      <DemographicListSection
        lists={[...DEMOGRAPHICS, ...PROFILE]}
        catalog={catalog}
        facilityId={realFacility}
      />

      <DemographicMismatchCard
        facilityId={realFacility}
        options={options.data ?? []}
        canManage={catalog.canManage}
      />

      {removing ? (
        <RemoveConfirm
          title={`Delete “${removing.name}”?`}
          confirmLabel="Delete option"
          description="Residents who already carry this value keep it, but it stops being offered for good. Switching it off hides it too and can be undone."
          onConfirm={() => remove.mutate({ optionId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
